import { Flex, Table, Text } from '@chakra-ui/react';
import { PredictionOpenOrder } from '@features/positions/predictions/types';
import { memo } from 'react';
import { LuChevronRight } from 'react-icons/lu';

interface OpenOrderRowProps {
  order: PredictionOpenOrder;
  index: number;
  totalCount: number;
  onClick: (order: PredictionOpenOrder) => void;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const OpenOrderRow = memo(({ order, index, totalCount, onClick }: OpenOrderRowProps) => (
  <Table.Row fontSize="sm" cursor="pointer" _hover={{ bg: 'bg.subtle' }} onClick={() => onClick(order)}>
    <Table.Cell
      border="none"
      p={4}
      borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
      borderBottomLeftRadius={index === totalCount - 1 ? 'xl' : 'none'}
    >
      <Flex direction="column" gap={0.5}>
        <Text fontWeight="medium">{order.market}</Text>
        <Text fontSize="sm" fontWeight="medium">You think: {order.outcome}</Text>
      </Flex>
    </Table.Cell>
    <Table.Cell border="none" p={4}>
      <Flex direction="column" gap={0.5}>
        <Text fontWeight="medium">{order.filled} / {order.total}</Text>
        <Text fontSize="xs" color="fg.subtle">shares filled</Text>
      </Flex>
    </Table.Cell>
    <Table.Cell border="none" p={4}>
      <Text>{formatDate(order.expiration)}</Text>
    </Table.Cell>
    <Table.Cell
      border="none"
      p={4}
      pr={2}
      borderTopRightRadius={index === 0 ? 'xl' : 'none'}
      borderBottomRightRadius={index === totalCount - 1 ? 'xl' : 'none'}
      w="1"
    >
      <LuChevronRight size={16} color="var(--chakra-colors-text-secondary)" />
    </Table.Cell>
  </Table.Row>
));
OpenOrderRow.displayName = 'OpenOrderRow';

interface Props {
  orders: PredictionOpenOrder[];
  onRowClick: (order: PredictionOpenOrder) => void;
}

const OpenOrdersTable = memo(({ orders, onRowClick }: Props) => {
  return (
    <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2} overflowX="auto">
      <Table.Root>
        <Table.Header fontSize="sm" fontWeight="medium">
          <Table.Row bg="transparent">
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>MARKET</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>FILLED</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>EXPIRES</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" p={4} w="1" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {orders.map((order, index) => (
            <OpenOrderRow
              key={order.id}
              order={order}
              index={index}
              totalCount={orders.length}
              onClick={onRowClick}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
});
OpenOrdersTable.displayName = 'OpenOrdersTable';

export default OpenOrdersTable;
